import { useRef } from 'react'

import { createAIConversation } from '../../../../api'
import { EMPTY_FUNCTION } from '../../../../constants/common'
import { useApi } from '../../../../hooks'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import { getGretaAIChatErrorStatus } from '../../GretaAIChat.utils'

import {
  extractGretaAIChatConversationId,
  getGretaAIChatDraftTitle,
} from './useGretaAIChatChatCreation.utils'

export function useGretaAIChatChatCreation({
  getIsNewChatViewActive,
  loadedChatIdRef,
  requestReply,
  setMessages,
  setStatus,
  onChatCreated = EMPTY_FUNCTION,
}) {
  const isCreatingChatRef = useRef(false)

  // Bumped on every creation attempt and on Stop, so a delayed error can tell it's been superseded.
  const creationAttemptRef = useRef(0)

  const createApi = useApi()

  async function createChatAndSend(pendingMessages) {
    if (!isCreatingChatRef.current) {
      isCreatingChatRef.current = true

      const attemptId = ++creationAttemptRef.current

      setMessages(pendingMessages)
      setStatus(GretaAIChatStatus.Thinking)

      try {
        const response = await createApi.call(createAIConversation, {
          data: { title: getGretaAIChatDraftTitle(pendingMessages) },
        })
        const conversationId = extractGretaAIChatConversationId(response)

        // Release the lock once the id is in hand, before the possibly-backgrounded
        // reply — else a second New Chat send stays blocked while the first replies.
        isCreatingChatRef.current = false

        // If the user left the new-chat screen while this was creating, let it run in the background.
        const isNewChatViewActive = getIsNewChatViewActive()

        if (conversationId) {
          if (isNewChatViewActive) {
            loadedChatIdRef.current = conversationId
          }

          onChatCreated({
            chatData: response.data,
            chatId: conversationId,
            shouldActivate: isNewChatViewActive,
          })

          await requestReply(conversationId, pendingMessages)
        } else if (response && isNewChatViewActive) {
          setStatus(GretaAIChatStatus.Error)
        }
      } catch (reason) {
        isCreatingChatRef.current = false

        if (
          getIsNewChatViewActive() &&
          creationAttemptRef.current === attemptId
        ) {
          const errorStatus = await getGretaAIChatErrorStatus(reason)

          if (
            getIsNewChatViewActive() &&
            creationAttemptRef.current === attemptId
          ) {
            setStatus(errorStatus)
          }
        }
      }
    }
  }

  function abortChatCreation() {
    creationAttemptRef.current += 1
    createApi.abort()
  }

  return { abortChatCreation, createChatAndSend }
}
